import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { MAX_MESSAGE_LENGTH } from './constants';
import { getLanguageFromURL } from '../i18n/useStrings';
import { formatString, TRANSLATIONS, EN_STRINGS } from '../i18n/strings';

/**
 * Configure marked options for Salesforce Messaging compatibility
 */
marked.use({
    breaks: true,        // Convert \n to <br> (important pour les sauts de ligne Salesforce)
    gfm: true,           // GitHub Flavored Markdown (listes, tableaux, etc.)
    headerIds: false,    // Pas besoin d'IDs sur les headers dans un chat
    mangle: false,       // Ne pas obscurcir les emails
    pedantic: false,     // Mode permissif pour mieux supporter les variations
    // Enable extensions for better URL handling
    extensions: [{
        name: 'plainUrl',
        level: 'inline',
        start(src) {
            // Find where http:// or https:// starts
            const match = src.match(/https?:\/\//);
            return match ? match.index : undefined;
        },
        tokenizer(src) {
            // Match URLs that start at the beginning of the string
            // Allow most characters except whitespace and common sentence terminators
            const rule = /^(https?:\/\/[^\s<>]+)/;
            const match = rule.exec(src);

            if (match) {
                let url = match[1];
                let raw = match[0];

                // Remove trailing punctuation that's typically not part of URLs
                // but keep trailing slashes, query params, and fragments
                const originalLength = url.length;
                while (/[.,;:!?)\]]$/.test(url) && !url.endsWith('/')) {
                    url = url.slice(0, -1);
                }

                // Adjust raw to match what we actually consumed
                if (url.length < originalLength) {
                    raw = url;
                }

                return {
                    type: 'plainUrl',
                    raw: raw,
                    text: url,
                    href: url
                };
            }
        },
        renderer(token) {
            return `<a href="${token.href}">${token.text}</a>`;
        }
    }]
});

/**
 * Configure DOMPurify to allow safe HTML elements for rich messaging
 */
const ALLOWED_TAGS = [
    'p', 'br', 'span', 'div',
    'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
    'blockquote', 'pre', 'code',
    'a', 'hr',
];

const ALLOWED_ATTR = ['href', 'title', 'target', 'rel', 'class'];

const DOMPURIFY_CONFIG = {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    // Force tous les liens à s'ouvrir dans un nouvel onglet (sécurité)
    ADD_ATTR: ['target'],
};

/**
 * Converts Markdown text to sanitized HTML.
 * Safe for rendering in WebView2 via dangerouslySetInnerHTML.
 *
 * @param {string} markdownText - Raw Markdown text from Salesforce
 * @returns {string} - Sanitized HTML string ready for rendering
 *
 * @example
 * const html = renderMarkdown("**Bold** text with *italic* and\n- List item 1\n- List item 2");
 * // Returns: "<p><strong>Bold</strong> text with <em>italic</em></p><ul><li>List item 1</li><li>List item 2</li></ul>"
 */
export function renderMarkdown(markdownText) {
    if (!markdownText || typeof markdownText !== 'string') {
        return '';
    }

    // Limite de sécurité pour éviter le traitement de messages trop longs
    if (markdownText.length > MAX_MESSAGE_LENGTH) {
        console.warn(
            `[markdownUtils] Message truncated: ${markdownText.length} chars exceeds max ${MAX_MESSAGE_LENGTH}`
        );

        // Get current language strings (can't use hook in non-React function)
        const lang = getLanguageFromURL();
        const strings = TRANSLATIONS[lang] || EN_STRINGS;
        const truncationMessage = formatString(strings.messageTruncated, MAX_MESSAGE_LENGTH.toLocaleString());

        markdownText = markdownText.substring(0, MAX_MESSAGE_LENGTH) + `\n\n---\n*[${truncationMessage}]*`;
    }

    try {
        // Étape 1 : Convertir Markdown → HTML brut (l'extension autolink gère les URLs)
        const rawHTML = marked.parse(markdownText);

        // Étape 2 : Sanitizer le HTML (protection XSS)
        const cleanHTML = DOMPurify.sanitize(rawHTML, DOMPURIFY_CONFIG);

        // Étape 3 : Post-traitement pour les liens (sécurité WebView2)
        const safeHTML = cleanHTML.replace(
            /<a /g,
            '<a target="_blank" rel="noopener noreferrer" '
        );

        return safeHTML;
    } catch (error) {
        console.error('[markdownUtils] Error rendering markdown:', error);
        // Fallback : retourner le texte brut échappé en cas d'erreur
        return DOMPurify.sanitize(markdownText, { ALLOWED_TAGS: [] });
    }
}

/**
 * Checks if a string contains Markdown syntax.
 * Useful to optimize rendering (skip processing for plain text).
 * 
 * @param {string} text - Text to check
 * @returns {boolean} - True if Markdown syntax detected
 */
export function containsMarkdown(text) {
    if (!text || typeof text !== 'string') {
        return false;
    }

    const markdownPatterns = [
        /\*\*.*\*\*/,           // **bold**
        /\*[^*]+\*/,            // *italic*
        /__.*__/,               // __bold__
        /_[^_]+_/,              // _italic_
        /^#{1,6}\s/m,           // # Headers
        /^\s*[-*+]\s/m,         // - lists
        /^\s*\d+\.\s/m,         // 1. numbered lists
        /\[.*\]\(.*\)/,         // [link](url)
        /^>{1,}\s/m,            // > blockquote
        /```/,                  // ```code blocks```
        /`[^`]+`/,              // `inline code`
        /^---$/m,               // --- separator
    ];

    return markdownPatterns.some(pattern => pattern.test(text));
}
